import Evented from "@ember/object/evented"
import Service, { inject as service } from "@ember/service"
import * as Sentry from "@sentry/browser"
import type { ActionDoc } from "@shared/models/action"
import ObjectId from "@shared/util/object-id-gen"
import Cookies from "js-cookie"
import { partial } from "lodash-es"
import {
  buildActionDoc,
  buildActionDocContext,
  type PreparedAction,
} from "../lib/action-doc-builder"
import allActions from "../lib/actions/all"
import type InternalEmberStore from "./internal-ember-store"
import type PatchCoordinator from "./patch-coordinator"
import type Session from "./session"

interface Persister {
  persist(actionDoc: ActionDoc): void
}

/**
 * The single write path for the app. Ember components inject this service and
 * call `dispatch(actionName, payload)`; React components reach it through the
 * `useDispatch()` hook. Dispatching an action updates both internal stores via
 * the patch coordinator, so neither store facade owns the write path.
 */
export default class Dispatcher extends Service.extend(Evented) {
  @service("internal-ember-store") declare internalEmberStore: InternalEmberStore
  @service declare persister: Persister
  @service("patch-coordinator") declare patchCoordinator: PatchCoordinator
  @service declare session: Session
  @service declare undo: any

  private sequenceNumber = 0
  private lastActionOccurredAt = new Date()

  /**
   * Looks an action up by name, builds it, and dispatches it. This is the entry
   * point components use to change data.
   */
  dispatch(actionName: string, payload: object): ActionDoc {
    let action = allActions[actionName]
    if (action === undefined) console.error(`${actionName} does not exist`, actionName)
    action.name = actionName

    return this.dispatchAction(action, payload)
  }

  /**
   * Assembles a prepared action from an action definition + payload, then
   * dispatches it.
   */
  dispatchAction(
    action: { name: string; patches?: any; undoPatches?: any; narrative?: any },
    payload: object
  ): ActionDoc {
    return this.dispatchPreparedAction({
      name: action.name,
      payload: payload,
      patches: action.patches ? action.patches(payload) : null,
      undoPatches: action.undoPatches ? action.undoPatches(payload) : null,
      narrative: action.narrative ? partial(action.narrative, payload) : null,
    })
  }

  dispatchPreparedAction(preparedAction: PreparedAction): ActionDoc {
    try {
      this.recordBeforeAction(preparedAction)
      this.sequenceNumber++
      const actionId = ObjectId.create()
      const now = new Date()
      const actionDoc = buildActionDoc(
        preparedAction,
        buildActionDocContext({
          actionId,
          createPatchId: () => ObjectId.create(),
          currentDocumentHref: document.location.href,
          currentUrl: window.location.href,
          fbc: Cookies.get("_fbc"),
          fbp: Cookies.get("_fbp"),
          findInMemory: this.internalEmberStore.findInMemory.bind(this.internalEmberStore),
          formatDate: dateFns.format,
          // FullStory is gone; the field stays because it is part of the persisted action doc.
          fullStorySessionUrl: null,
          groupId: this.session.fiscalGroupId,
          lastActionOccurredAt: this.lastActionOccurredAt,
          now,
          sequenceNumber: this.sequenceNumber,
          sessionId: this.session.id,
          userId: this.session.userId,
        })
      )

      this.commitActionDoc(preparedAction, actionId, actionDoc)
      this.lastActionOccurredAt = now
      return actionDoc
    } finally {
      Sentry.setContext("Prepared Action", null)
    }
  }

  private recordBeforeAction(preparedAction: PreparedAction): void {
    Sentry.addBreadcrumb({
      message: preparedAction.name,
      category: "beforeAction",
    })

    Sentry.setContext("Prepared Action", {
      payload: preparedAction.payload,
      patches: "here",
    })
  }

  private commitActionDoc(
    preparedAction: PreparedAction,
    actionId: string,
    actionDoc: ActionDoc
  ): void {
    this.undo.addWithPatches(actionId, preparedAction.undoPatches, actionDoc.attributes.narrative)
    this.patchCoordinator.applyLocalActionPatches(actionDoc)
    this.persister.persist(actionDoc)
    this.trigger("action", preparedAction.name, preparedAction.payload, actionDoc)
  }
}

declare module "@ember/service" {
  interface Registry {
    dispatcher: Dispatcher
  }
}
